import { useState, useEffect, useRef, useCallback } from 'react';
import { useServiceWorkerUpdate } from '@/hooks/useServiceWorkerUpdate';

/**
 * サービスワーカーのバージョンチェックと更新を行うコンポーネント
 * @returns
 */
const UpdatePrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const updateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const intervalMS = 3 * 60 * 1000; // 3分

  const { needRefresh, updateServiceWorker, checkForUpdates, hasUpdate, isUpdating } = useServiceWorkerUpdate();

  const updateApp = useCallback(async () => {
    if (isUpdating) {
      console.log('Update already in progress, ignoring click');
      return;
    }

    console.log('Updating application...');
    try {
      await updateServiceWorker();
      console.log('Service worker update completed successfully');
    } catch (err) {
      console.error('Failed to update:', err);
    }
  }, [updateServiceWorker, isUpdating]);

  // needRefreshステートとhasUpdateステートを監視
  useEffect(() => {
    if (needRefresh[0] || hasUpdate) {
      setShowPrompt(true);
    }
  }, [needRefresh, hasUpdate]);

  useEffect(() => {
    const performUpdateCheck = async () => {
      try {
        const hasUpdate = await checkForUpdates();
        if (hasUpdate) {
          setShowPrompt(true);
        }
      } catch (error) {
        console.error('Error during update check:', error);
      }
    };

    // 最初に一度チェック
    performUpdateCheck();

    // 修正: 非同期関数をsetInterval内で呼び出す
    updateIntervalRef.current = setInterval(() => {
      performUpdateCheck();
    }, intervalMS);

    // コンポーネントのアンマウント時にインターバルをクリア
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, [checkForUpdates]);

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="mx-2 w-fit flex items-center bg-gray-900 opacity-90 text-gray-400 text-sm shadow rounded-lg px-6 py-4 select-none">
      <div>最新のバージョンがあります。更新してください。</div>
      <div className="border-r border-gray-700 h-8 mx-6"></div>
      <button
        className={`min-w-14 flex justify-center items-center transition-colors ${
          isUpdating ? 'text-gray-500 cursor-not-allowed' : 'hover:text-blue-500 cursor-pointer'
        }`}
        type="button"
        onClick={updateApp}
        disabled={isUpdating}
        aria-label="アプリケーションを更新">
        <svg
          className={`w-4 h-4 me-1 ${isUpdating ? 'animate-spin' : ''}`}
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24">
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M17.651 7.65a7.131 7.131 0 0 0-12.68 3.15M18.001 4v4h-4m-7.652 8.35a7.13 7.13 0 0 0 12.68-3.15M6 20v-4h4"
          />
        </svg>
        {isUpdating ? '更新' : '更新'}
      </button>
    </div>
  );
};

export default UpdatePrompt;
